/**
 * ============================================
 * USE SUCURSALES - Hooks para gestión de sucursales
 * ============================================
 *
 * Hooks de TanStack Query para:
 * - Listar sucursales (useQuery)
 * - Crear sucursal (useMutation)
 * - Actualizar sucursal (useMutation)
 * - Eliminar sucursal (useMutation)
 * - Activar/Desactivar sucursal (useMutation)
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import {
  getSucursales,
  createSucursal,
  updateSucursal,
  deleteSucursal,
  activateSucursal,
  deactivateSucursal,
  type SucursalCreateData,
  type SucursalUpdateData,
} from "@/services/sucursales";

// Re-export types
export type { Sucursal, SucursalCreateData, SucursalUpdateData } from "@/services/sucursales";

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Hook para cargar la lista de sucursales
 */
export function useSucursales(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.sucursales,
    queryFn: getSucursales,
    enabled: options?.enabled ?? true,
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

interface MutationCallbacks {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook para crear una nueva sucursal
 */
export function useCreateSucursal(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SucursalCreateData) => createSucursal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sucursales });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para actualizar una sucursal
 */
export function useUpdateSucursal(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SucursalUpdateData }) =>
      updateSucursal(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sucursales });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para eliminar una sucursal
 */
export function useDeleteSucursal(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteSucursal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sucursales });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para activar una sucursal
 */
export function useActivateSucursal(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => activateSucursal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sucursales });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}

/**
 * Hook para desactivar una sucursal
 */
export function useDeactivateSucursal(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deactivateSucursal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sucursales });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => {
      callbacks?.onError?.(error);
    },
  });
}
